import axios, {
  type AxiosError,
  type AxiosInstance,
  type AxiosRequestConfig,
  type InternalAxiosRequestConfig,
} from 'axios';

import type { AuthTokens } from '@/entities/user';
import { env } from '@/shared/config/env';
import {
  clearTokens,
  getAccessToken,
  getPersistedUserSnapshot,
  getRefreshToken,
  getSessionSnapshot,
  newIdempotencyKey,
  readJwtSubject,
  setTokens,
  shouldAttachIdempotencyKey,
} from '@/shared/lib/auth';

import { unwrapEnvelopeData, type RefreshTokensPayload } from './apiEnvelope';
import { toApiError } from './errors';

/**
 * Shared axios instance. Features never create their own client; they use
 * this one via `features/<name>/api/*`.
 *
 * Responsibilities (TECHNICAL_DESIGN.md §3.8):
 *   - Attach `Authorization: Bearer <access>` from tokenStorage.
 *   - Attach `Idempotency-Key` on money-moving POST/PUT/PATCH/DELETE.
 *   - Reactively refresh on 401, single-flighted across concurrent requests.
 *   - Normalise errors to `ApiError` for the rest of the app.
 */

declare module 'axios' {
  interface InternalAxiosRequestConfig {
    _retry?: boolean;
    /** Set `false` to skip the Idempotency-Key heuristic on a specific call. */
    idempotency?: boolean;
  }
  interface AxiosRequestConfig {
    idempotency?: boolean;
  }
}

/** Single-flight refresh — concurrent 401s share one POST refresh path. */
let refreshInFlight: Promise<AuthTokens> | null = null;

/** Hook invoked when refresh fails; wired up by the app shell at boot. */
type HardLogoutHandler = (reason: 'refresh_failed' | 'no_refresh_token') => void;
let hardLogoutHandler: HardLogoutHandler = () => {
  clearTokens();
};

export function setHardLogoutHandler(handler: HardLogoutHandler): void {
  hardLogoutHandler = handler;
}

export const http: AxiosInstance = axios.create({
  baseURL: env.apiBaseUrl,
  withCredentials: false,
  timeout: 20_000,
});

http.interceptors.request.use((cfg) => {
  const token = getAccessToken();
  if (token) {
    cfg.headers = cfg.headers ?? {};
    (cfg.headers as Record<string, string>).Authorization = `Bearer ${token}`;
  }

  const wantKey =
    cfg.idempotency !== false &&
    (cfg.idempotency === true || shouldAttachIdempotencyKey(cfg.method ?? 'get', cfg.url ?? ''));

  if (wantKey) {
    cfg.headers = cfg.headers ?? {};
    const headers = cfg.headers as Record<string, string>;
    headers['Idempotency-Key'] = headers['Idempotency-Key'] ?? newIdempotencyKey();
  }

  return cfg;
});

function isAuthMutationNo401Replay(url: string | undefined): boolean {
  if (!url) return false;
  return (
    url.includes('/auth/refresh') ||
    url.includes('/auth/logout') ||
    url.includes('super-admin/login')
  );
}

http.interceptors.response.use(
  (r) => r,
  async (err: AxiosError) => {
    const original = err.config as InternalAxiosRequestConfig | undefined;
    const skip401RefreshReplay = isAuthMutationNo401Replay(original?.url);

    if (err.response?.status === 401 && original && !original._retry && !skip401RefreshReplay) {
      original._retry = true;
      try {
        const tokens = await runRefresh();
        setTokens(tokens);
        if (original.headers) {
          (original.headers as Record<string, string>).Authorization =
            `Bearer ${tokens.accessToken}`;
        }
        return http.request(original);
      } catch (refreshErr) {
        hardLogoutHandler('refresh_failed');
        return Promise.reject(toApiError(refreshErr));
      }
    }

    return Promise.reject(toApiError(err));
  },
);

async function runRefresh(): Promise<AuthTokens> {
  if (refreshInFlight) return refreshInFlight;

  const refreshToken = getRefreshToken();
  if (!refreshToken) {
    hardLogoutHandler('no_refresh_token');
    throw new Error('No refresh token available');
  }

  refreshInFlight = (async () => {
    try {
      const userId = resolveRefreshUserId();
      if (!userId) {
        throw new Error('No user id available for token refresh');
      }

      const base = env.apiBaseUrl.replace(/\/$/, '');
      const path = env.authRefreshPath.startsWith('/')
        ? env.authRefreshPath
        : `/${env.authRefreshPath}`;
      const { data: body, status } = await axios.post<unknown>(
        `${base}${path}`,
        { refreshToken, userId },
        { timeout: 10_000 },
      );
      const payload = unwrapEnvelopeData<RefreshTokensPayload>(body, status);
      return normalizeRefreshTokens(payload, refreshToken);
    } finally {
      refreshInFlight = null;
    }
  })();

  return refreshInFlight;
}

function resolveRefreshUserId(): string | null {
  const fromSession = getSessionSnapshot().user?.id?.trim();
  if (fromSession) return fromSession;
  const fromSnapshot = getPersistedUserSnapshot()?.id?.trim();
  if (fromSnapshot) return fromSnapshot;
  return readJwtSubject(getAccessToken());
}

function normalizeRefreshTokens(
  payload: RefreshTokensPayload | Record<string, unknown>,
  previousRefreshToken: string,
): AuthTokens {
  const o = payload && typeof payload === 'object' ? (payload as Record<string, unknown>) : {};
  const accessToken =
    (typeof o.accessToken === 'string' && o.accessToken) ||
    (typeof o.access_token === 'string' && o.access_token) ||
    '';
  const refreshToken =
    (typeof o.refreshToken === 'string' && o.refreshToken) ||
    (typeof o.refresh_token === 'string' && o.refresh_token) ||
    previousRefreshToken;
  if (!accessToken) {
    throw new Error('Refresh response missing accessToken');
  }
  return { accessToken, refreshToken };
}

/**
 * Typed request helpers. Prefer these over `http.get/post` in feature code —
 * they force a generic and return unwrapped data.
 */
export const api = {
  get: <T>(url: string, cfg?: AxiosRequestConfig) => http.get<T>(url, cfg).then((r) => r.data),
  post: <T>(url: string, body?: unknown, cfg?: AxiosRequestConfig) =>
    http.post<T>(url, body, cfg).then((r) => r.data),
  put: <T>(url: string, body?: unknown, cfg?: AxiosRequestConfig) =>
    http.put<T>(url, body, cfg).then((r) => r.data),
  patch: <T>(url: string, body?: unknown, cfg?: AxiosRequestConfig) =>
    http.patch<T>(url, body, cfg).then((r) => r.data),
  delete: <T>(url: string, cfg?: AxiosRequestConfig) =>
    http.delete<T>(url, cfg).then((r) => r.data),
};
